import React, { useState, useEffect } from 'react';
import { Typography, Tabs, Row, Col, Badge, Layout } from 'antd';

import { ClockCircleOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';

//Component imports
import INTERNAL_LINKS from '../../enums/InternalLinks';
import BreadcrumbContainer from '../../containers/Breadcrumb';
import CustomButton from '../../components/CustomButton';
import { catchException } from '../../utils/helpers/helpers';
import {
  getCounters,
  projectSelector,
  roleSelector,
  rejectionsTDR,
  rejectionsCTM,
  getCountersCTM,
} from '../../api/module/rejections';
import { AjaxTable, CustomSelector } from '../../components';

const { TabPane } = Tabs;
const { Title, Paragraph } = Typography;
const { Content } = Layout;

const breadcrumbItems = [
  {
    text: 'Rechazo de TDR',
  },
];

const initCounters = () => ({
  pending: (
    <ClockCircleOutlined style={{ marginLeft: '13px', marginRight: '3px' }} />
  ),
  confirmed: (
    <ClockCircleOutlined style={{ marginLeft: '13px', marginRight: '3px' }} />
  ),
});

const RejectTDR = ({ match }) => {
  const [counters, setCounters] = useState(initCounters);
  const [countersCTM, setCountersCTM] = useState(initCounters);

  const [activeTab, setActiveTab] = useState(match.params.tab || 'pending');

  const [query, setQuery] = useState(
    match.params.project_id
      ? { proyecto: parseInt(match.params.project_id) }
      : {},
  );

  async function fetchCounters(newTab) {
    try {
      setCounters(initCounters);
      const counters = await getCounters(query);
      setCounters(counters.data.results);
      if (newTab !== activeTab) setActiveTab(newTab);
    } catch (err) {
      catchException(err);
    }
  }
  async function fetchCountersCTM(newTab) {
    try {
      setCountersCTM(initCounters);
      const counters = await getCountersCTM(query);
      setCountersCTM(counters.data.results);
      if (newTab !== activeTab) setActiveTab(newTab);
    } catch (err) {
      catchException(err);
    }
  }

  useEffect(() => {
    fetchCounters(activeTab);
    fetchCountersCTM(activeTab);
  }, [JSON.stringify(query)]);

  const columns = [
    {
      title: 'Proyecto',
      dataIndex: 'proyecto_nombre',
    },
    {
      title: 'Cargo',
      dataIndex: 'cargo_nombre',
    },
    {
      title: 'Contratista',
      dataIndex: 'contratista_nombre',
    },
    {
      title: 'Fecha de inicio',
      dataIndex: 'fecha_inicio',
      width: '7rem',
      align: 'center',
    },
    {
      title: 'Fecha de inicio max',
      dataIndex: 'fecha_maxima_inicio',
      width: '7rem',
      align: 'center',
    },
    {
      title: 'Fecha de terminación',
      dataIndex: 'fecha_fin',
      width: '7rem',
      align: 'center',
    },
    {
      title: 'Fecha de terminación max',
      dataIndex: 'fecha_maxima_fin',
      width: '7rem',
      align: 'center',
    },
  ];

  const extraColumnsToCheck = [
    {
      title: 'Acciones',
      key: 'acciones',
      width: '5rem',
      fixed: 'right',
      render: (_, record) => (
        <CustomButton
          block
          type="primary"
          htmlType={'button'}
          text={
            <Link
              to={`${
                activeTab.endsWith('CTM')
                  ? INTERNAL_LINKS.REJECT_CTM
                  : INTERNAL_LINKS.REJECT_TDR
              }-revisar/${record.id}`}
            >
              Revisar
            </Link>
          }
        />
      ),
    },
  ];

  const extraColumnsConfirmed = [
    {
      title: 'Usuario que confirmó',
      dataIndex: 'cur_fullname',
    },
  ];

  return (
    <Content>
      <BreadcrumbContainer items={breadcrumbItems} />
      <Title className={'text-uppercase'} level={2}>
        Rechazos TDR y Modificación Contractual
      </Title>
      <br />
      <Row className="box-selectors">
        <Col span={6}>
          <Title level={4}>Proyecto </Title>
        </Col>

        <Col span={17} offset={0.5}>
          <CustomSelector
            onChange={(v) => {
              setQuery(v ? { proyecto: v } : {});
            }}
            endpointFetch={async () => {
              return await projectSelector();
            }}
            selectorProps={{
              style: { width: '100%' },
              placeholder: 'Seleccione el Proyecto...',
            }}
            initialValue={query.proyecto}
          />
        </Col>
      </Row>
      {query.proyecto && (
        <Row className="box-selectors">
          <Col span={6}>
            <Title level={4}>Cargo </Title>
          </Col>

          <Col span={17} offset={0.5}>
            <CustomSelector
              onChange={(v) =>
                setQuery((q) => ({ proyecto: q.proyecto, cargo: v }))
              }
              endpointFetch={async () => {
                return await roleSelector(query.proyecto);
              }}
              selectorProps={{
                style: { width: '100%' },
                placeholder: 'Seleccione el Cargo...',
              }}
              initialValue={query.cargo}
              key={query.proyecto}
            />
          </Col>
        </Row>
      )}
      <br />
      <Tabs
        defaultActiveKey={'pending'}
        activeKey={activeTab}
        onTabClick={(key, _) => {
          setActiveTab(key);
        }}
        destroyInactiveTabPane={true}
        type="card"
      >
        <TabPane
          tab={
            <Paragraph>
              TDR contratista
              <Badge showZero count={counters.pending} />
            </Paragraph>
          }
          key="pending"
        >
          <AjaxTable
            columns={[...columns, ...extraColumnsToCheck]}
            endpoint={rejectionsTDR}
            query={{ ...query, pendiente: true }}
            onReload={() => fetchCounters('pending')}
            searchInput
            exportCSV
          />
        </TabPane>
        <TabPane
          tab={
            <Paragraph>
              Confirmados TDR
              <Badge showZero count={counters.confirmed} />
            </Paragraph>
          }
          key="confirmed"
        >
          <AjaxTable
            columns={[...columns, ...extraColumnsConfirmed]}
            endpoint={rejectionsTDR}
            query={{ ...query, pendiente: false }}
            onReload={() => fetchCounters('confirmed')}
            searchInput
            exportCSV
          />
        </TabPane>
        <TabPane
          tab={
            <Paragraph>
              Modificación Contractual contratista
              <Badge showZero count={countersCTM.pending} />
            </Paragraph>
          }
          key="pendingCTM"
        >
          <AjaxTable
            columns={[...columns, ...extraColumnsToCheck]}
            endpoint={rejectionsCTM}
            query={{ ...query, pendiente: true }}
            onReload={() => fetchCountersCTM('pendingCTM')}
            searchInput
            exportCSV
          />
        </TabPane>
        <TabPane
          tab={
            <Paragraph>
              Confirmados Modificación Contractual
              <Badge showZero count={countersCTM.confirmed} />
            </Paragraph>
          }
          key="confirmedCTM"
        >
          <AjaxTable
            columns={[...columns, ...extraColumnsConfirmed]}
            endpoint={rejectionsCTM}
            query={{ ...query, pendiente: false }}
            onReload={() => fetchCountersCTM('confirmedCTM')}
            searchInput
            exportCSV
          />
        </TabPane>
      </Tabs>
    </Content>
  );
};

export default RejectTDR;
